import React, { useState } from 'react';
import {
  X,
  Users,
  FileText,
  CheckCircle,
  User,
  UserCircle,
  DollarSign,
  Briefcase,
  Package,
  Mail,
  Calendar,
  Phone,
  MapPin,
  Building,
  ShoppingCart,
  CreditCard,
  Truck
} from 'lucide-react';
import { useCreateObject } from '../../services/queries/objectQueries';
import { useToast } from '../../contexts/ToastContext';
import { useSetupChanges } from '../../hooks/useSetupChanges';
import { StandardButton, StandardInput, StandardTextarea } from '../standard';
import { getCustomSuffix } from '../../config/gobodoConfig';

interface CreateObjectModalProps {
  onClose: () => void;
  onObjectCreated?: () => void; // Callback when an object is successfully created
}

// Available icons for object selection
const AVAILABLE_ICONS = [
  { name: 'Users', component: Users },
  { name: 'User', component: User },
  { name: 'UserCircle', component: UserCircle },
  { name: 'FileText', component: FileText },
  { name: 'CheckCircle', component: CheckCircle },
  { name: 'DollarSign', component: DollarSign },
  { name: 'Briefcase', component: Briefcase },
  { name: 'Package', component: Package },
  { name: 'Mail', component: Mail },
  { name: 'Calendar', component: Calendar },
  { name: 'Phone', component: Phone },
  { name: 'MapPin', component: MapPin },
  { name: 'Building', component: Building },
  { name: 'ShoppingCart', component: ShoppingCart },
  { name: 'CreditCard', component: CreditCard },
  { name: 'Truck', component: Truck }
];

export const CreateObjectModal: React.FC<CreateObjectModalProps> = ({ onClose, onObjectCreated }) => {
  const createObjectMutation = useCreateObject();
  const { success, error } = useToast();
  const { logChange } = useSetupChanges();

  const [formData, setFormData] = useState({
    label: '',
    apiName: '',
    pluralLabel: '',
    description: '',
    icon: 'Package',
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiNameManuallyEdited, setApiNameManuallyEdited] = useState(false);
  const [pluralLabelManuallyEdited, setPluralLabelManuallyEdited] = useState(false);

  // Auto-generate API name and plural label from label (only if not manually edited)
  const handleLabelChange = (label: string) => {
    setFormData(prev => {
      // Generate API name from label if it hasn't been manually edited
      let newApiName = prev.apiName;
      if (!apiNameManuallyEdited) {
        // Generate without suffix - suffix will be added on submit
        newApiName = label
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .replace(/^_|_$/g, '');
      }
      
      // Generate plural label from label if it hasn't been manually edited
      let newPluralLabel = prev.pluralLabel;
      if (!pluralLabelManuallyEdited) {
        newPluralLabel = label + 's';
      }
      
      return {
        ...prev,
        label,
        apiName: newApiName,
        pluralLabel: newPluralLabel
      };
    });
  };

  // Handle manual API name changes
  const handleApiNameChange = (apiName: string) => {
    setApiNameManuallyEdited(true);
    setFormData(prev => ({ ...prev, apiName }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    // Validation regex: must start with a letter and contain only letters, numbers, and underscores
    // Note: Suffix is automatically appended on submit, so we don't validate for it here
    const apiNameRegex = /^[A-Za-z][A-Za-z0-9_]*$/;

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (!formData.apiName.trim()) {
      newErrors.apiName = 'API Name is required';
    } else if (!apiNameRegex.test(formData.apiName)) {
      newErrors.apiName = 'API Name must start with a letter and contain only letters, numbers, and underscores';
    }

    if (!formData.pluralLabel.trim()) {
      newErrors.pluralLabel = 'Plural Label is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Ensure API name is lowercase and has suffix before submission
      const suffix = getCustomSuffix();
      let normalizedApiName = formData.apiName.toLowerCase().trim();
      
      // Ensure suffix is present
      if (!normalizedApiName.endsWith(suffix)) {
        normalizedApiName = normalizedApiName + suffix;
      }
      
      await createObjectMutation.mutateAsync({
        ...formData,
        apiName: normalizedApiName,
        isCustom: true,
        category: 'custom'
      });

      // Track object creation
      logChange({
        action: 'created',
        entityType: 'object',
        entityName: formData.label,
        description: `created custom object ${formData.label}`,
        performedBy: 'Admin'
      });

      success('Object created successfully!', `${formData.label} has been created`);
      
      // Call the onObjectCreated callback if provided (to refetch the objects list)
      if (onObjectCreated) {
        onObjectCreated();
      }
      
      onClose();
    } catch (err: any) {
      console.error('Failed to create object:', err);
      error('Failed to create object', err.message || 'Please try again');
      setErrors({ submit: 'Failed to create object. Please try again.' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Create New Object</h2>
          <StandardButton
            variant="ghost"
            onClick={onClose}
          >
            <X className="w-6 h-6" />
          </StandardButton>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-400">{errors.submit}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Basic Information</h3>

            {/* Label */}
            <StandardInput
              label="Label"
              required
              value={formData.label}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder="e.g., Product, Invoice, Support Ticket"
              error={errors.label}
            />

            {/* API Name */}
            <div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <StandardInput
                    label="API Name"
                    required
                    value={formData.apiName}
                    onChange={(e) => handleApiNameChange(e.target.value)}
                    placeholder={`e.g., Product${getCustomSuffix()}`}
                    error={errors.apiName}
                    helpText={`Used in API calls. Must be lowercase with underscores. Suffix ${getCustomSuffix()} will be added automatically if not present.`}
                  />
                </div>
                <span className="text-gray-500 dark:text-gray-400 text-sm mt-6">{getCustomSuffix()}</span>
              </div>
            </div>

            {/* Plural Label */}
            <StandardInput
              label="Plural Label"
              required
              value={formData.pluralLabel}
              onChange={(e) => {
                setPluralLabelManuallyEdited(true);
                setFormData(prev => ({ ...prev, pluralLabel: e.target.value }));
              }}
              placeholder="e.g., Products"
              error={errors.pluralLabel}
            />

            {/* Description */}
            <StandardTextarea
              label="Description"
              required
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this object is used for..."
              rows={3}
              error={errors.description}
            />
          </div>

          {/* Icon Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Icon</h3>
            <div className="grid grid-cols-8 gap-2">
              {AVAILABLE_ICONS.map(({ name, component: Icon }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, icon: name }))}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.icon === name
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600'
                  }`}
                  title={name}
                >
                  <Icon className={`w-6 h-6 mx-auto ${
                    formData.icon === name
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`} />
                </button>
              ))}
            </div>
          </div>

          {/* Status Toggle */}
          <div className="flex items-center justify-between py-4 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Enable this object for use immediately
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.isActive ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardButton
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </StandardButton>
            <StandardButton
              type="submit"
              variant="primary"
              disabled={createObjectMutation.isPending}
              isLoading={createObjectMutation.isPending}
            >
              {createObjectMutation.isPending ? 'Creating...' : 'Create Object'}
            </StandardButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateObjectModal;
